/**
 * Design System - Input Component
 *
 * A flexible input component with label, error, hint, and icon support.
 *
 * @example
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="Enter your email"
 *   required
 * />
 *
 * @example
 * <Input
 *   label="Password"
 *   type="password"
 *   error="Password must be at least 8 characters"
 *   hint="Use a strong password"
 * />
 */

import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { cn } from '../utils/classNames';

const Input = forwardRef(({
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  required = false,
  disabled = false,
  fullWidth = true,
  className,
  id,
  type = 'text',
  ...props
}, ref) => {
  // Generate unique ID if not provided
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  // Base input styles
  const baseStyles = 'px-3 py-2 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 text-gray-900 placeholder-gray-400';

  // State styles
  const stateStyles = error
    ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500'
    : 'border-gray-300 focus:border-primary-500';

  // Disabled styles
  const disabledStyles = disabled
    ? 'bg-gray-100 cursor-not-allowed opacity-60'
    : 'bg-white';

  // Width styles
  const widthStyle = fullWidth ? 'w-full' : '';

  // Icon padding
  const iconPaddingLeft = leftIcon ? 'pl-10' : '';
  const iconPaddingRight = rightIcon ? 'pr-10' : '';

  return (
    <div className={cn('form-field', className)}>
      {/* Label */}
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && (
            <span className="text-danger-500 ml-1" aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      {/* Input container */}
      <div className="relative">
        {/* Left icon */}
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-400" aria-hidden="true">
              {leftIcon}
            </span>
          </div>
        )}

        {/* Input */}
        <input
          ref={ref}
          id={inputId}
          type={type}
          disabled={disabled}
          aria-invalid={!!error}
          aria-required={required}
          aria-describedby={cn(
            error && errorId,
            hint && hintId
          )}
          className={cn(
            baseStyles,
            stateStyles,
            disabledStyles,
            widthStyle,
            iconPaddingLeft,
            iconPaddingRight
          )}
          {...props}
        />

        {/* Right icon */}
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <span className="text-gray-400" aria-hidden="true">
              {rightIcon}
            </span>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-1 text-sm text-danger-600"
        >
          {error}
        </p>
      )}

      {/* Hint text */}
      {hint && !error && (
        <p
          id={hintId}
          className="mt-1 text-sm text-gray-600"
        >
          {hint}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  label: PropTypes.string,
  error: PropTypes.string,
  hint: PropTypes.string,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
};

export default Input;
